import * as React from "react";
import { CheckIcon, ChevronsUpDownIcon } from "lucide-react";
import * as RPNInput from "react-phone-number-input";
import flags from "react-phone-number-input/flags";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { useIsMobile } from "../../lib/use-mobile";
import { MobilePickerSheet } from "./mobile-picker";

type CountrySelectProps = {
  value?: RPNInput.Country;
  onChange: (value?: RPNInput.Country) => void;
  disabled?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyLabel?: string;
  className?: string;
  compact?: boolean;
  showCallingCode?: boolean;
};

function getCountryLabel(country: RPNInput.Country): string {
  try {
    const display = new Intl.DisplayNames(["en"], { type: "region" });
    return display.of(country) ?? country;
  } catch {
    return country;
  }
}

function FlagComponent({ country, countryName }: RPNInput.FlagProps) {
  const Flag = country ? flags[country] : undefined;
  return <span className='flex h-4 w-6 overflow-hidden rounded-sm'>{Flag ? <Flag title={countryName} /> : null}</span>;
}

export function CountrySelect({
  value,
  onChange,
  disabled,
  placeholder = "Select country",
  searchPlaceholder = "Search country...",
  emptyLabel = "No country found.",
  className,
  compact = false,
  showCallingCode = false,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const isMobile = useIsMobile();
  const countries = React.useMemo(
    () =>
      RPNInput.getCountries().map((country) => ({
        value: country,
        label: getCountryLabel(country),
      })),
    [],
  );

  const selectedLabel = value ? getCountryLabel(value) : placeholder;
  const selectedCallingCode = value ? `+${RPNInput.getCountryCallingCode(value)}` : "";

  const triggerClassName = cn(
    buttonVariants({ variant: "outline" }),
    compact
      ? "min-w-[5.75rem] justify-between gap-2 rounded-e-none border-e-0 px-2.5 shadow-none"
      : "w-full justify-between",
    className,
  );
  const triggerInner = (
    <>
      <span className='flex min-w-0 items-center gap-2'>
        {value ? <FlagComponent country={value} countryName={selectedLabel} /> : null}
        {compact ? (
          <span className='truncate text-left text-sm font-normal'>{showCallingCode ? selectedCallingCode : selectedLabel}</span>
        ) : (
          <span className='truncate text-left font-normal'>{selectedLabel}</span>
        )}
      </span>
      <ChevronsUpDownIcon className='text-muted-foreground size-4 shrink-0' />
    </>
  );

  if (isMobile) {
    // Phone: the app's bottom sheet list (search on top, flag and calling code on each row), not a popover.
    return (
      <>
        <button type='button' disabled={disabled} className={triggerClassName} onClick={() => setOpen(true)} aria-haspopup='dialog'>
          {triggerInner}
        </button>
        <MobilePickerSheet
          open={open}
          onOpenChange={setOpen}
          title={placeholder}
          value={value ?? null}
          options={countries.map((country) => ({
            value: country.value,
            label: country.label,
            searchText: `${country.label} ${country.value} +${RPNInput.getCountryCallingCode(country.value)}`,
            description: `+${RPNInput.getCountryCallingCode(country.value)}`,
            leading: <FlagComponent country={country.value} countryName={country.label} />,
          }))}
          onSelect={(next) => onChange(next as RPNInput.Country)}
          searchPlaceholder={searchPlaceholder}
          emptyLabel={emptyLabel}
          searchable
        />
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {/* Style the trigger with `buttonVariants` directly (not the `Button` component) so the label and chevron
          stay as real flex children — `Button` wraps its children in one left-aligned span, which would defeat
          `justify-between` and glue the chevron to the label. */}
      <PopoverTrigger type='button' disabled={disabled} className={triggerClassName}>
        {triggerInner}
      </PopoverTrigger>
      <PopoverContent className='w-[320px] p-0' align='start'>
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyLabel}</CommandEmpty>
            <CommandGroup>
              {countries.map((country) => (
                <CommandItem
                  key={country.value}
                  value={`${country.label} ${country.value}`}
                  onSelect={() => {
                    onChange(country.value);
                    setOpen(false);
                  }}
                >
                  <FlagComponent country={country.value} countryName={country.label} />
                  <span className='flex-1 text-sm'>{country.label}</span>
                  <CheckIcon className={cn("size-4", value === country.value ? "opacity-100" : "opacity-0")} />
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
